import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormSelect } from '@/components/form/FormSelect'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { StudentLanguage } from '@/features/student/api/profile.api'
import { useLanguageMutations } from '@/features/student/hooks/useProfile'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { humanizeEnum } from '@/utils/format'

const PROFICIENCY_OPTIONS = ['basic', 'conversational', 'fluent', 'native'].map((value) => ({
  value,
  label: humanizeEnum(value),
}))

const schema = z.object({
  language_name: z.string().min(1, 'Language is required'),
  proficiency_level: z.string().min(1, 'Proficiency is required'),
})

type FormValues = z.infer<typeof schema>

function toFormValues(item?: StudentLanguage): FormValues {
  return {
    language_name: item?.languageName ?? '',
    proficiency_level: item?.proficiencyLevel ?? '',
  }
}

export function LanguageSection({ items }: { items: StudentLanguage[] }) {
  const [editing, setEditing] = useState<StudentLanguage | 'new' | null>(null)
  const [apiError, setApiError] = useState<unknown>(null)
  const { create, update, remove } = useLanguageMutations()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues() })

  useEffect(() => {
    reset(toFormValues(editing === 'new' || editing === null ? undefined : editing))
  }, [editing, reset])

  const isPending = create.isPending || update.isPending

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    const onSettled = { onSuccess: () => setEditing(null), onError: (e: unknown) => setApiError(e) }
    if (editing === 'new') {
      create.mutate(values, onSettled)
    } else if (editing) {
      update.mutate({ id: editing.id, payload: values }, onSettled)
    }
  })

  return (
    <div>
      {items.length === 0 && <p className="text-muted fs-13">No languages added yet.</p>}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {items.map((item) => (
          <span
            key={item.id}
            className="badge bg-light text-dark border d-flex align-items-center gap-2 p-2"
          >
            {item.languageName} ({humanizeEnum(item.proficiencyLevel)})
            <button
              type="button"
              className="btn-close fs-10"
              aria-label={`Edit ${item.languageName}`}
              onClick={() => setEditing(item)}
            />
          </span>
        ))}
      </div>
      <Button variant="secondary" onClick={() => setEditing('new')}>
        Add Language
      </Button>

      <Modal
        show={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Language' : 'Edit Language'}
      >
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(apiError)}
          generalMessage={apiError ? getErrorMessage(apiError) : null}
        />
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormInput control={control} name="language_name" label="Language" required />
          <FormSelect
            control={control}
            name="proficiency_level"
            label="Proficiency"
            options={PROFICIENCY_OPTIONS}
            required
          />
          <div className="d-flex gap-2">
            <Button type="submit" className="flex-grow-1" loading={isPending}>
              Save
            </Button>
            {editing !== 'new' && editing && (
              <Button
                variant="danger"
                confirmTitle="Remove this language?"
                onClick={() => remove.mutate(editing.id, { onSuccess: () => setEditing(null) })}
              >
                Delete
              </Button>
            )}
          </div>
        </form>
      </Modal>
    </div>
  )
}
